var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateIssueStatus, bulkUpdateIssues, bulkMarkIssuesForSuggestion } from '../services/IIMIssuesService';
import { IssueStatusEnum } from '../types';
import { iimReviewKeys } from './queryKeys';
// Statuses that can be acted on via bulk operations (suggest/dismiss) — resolved requires undo first
const ACTIONABLE_STATUSES = [IssueStatusEnum.Detected, IssueStatusEnum.Skipped, IssueStatusEnum.DismissedByTech];
/**
 * Hook for updating a single IIM issue with optimistic updates
 * @returns Mutation object for updating issue status and manager notes
 */
export function useUpdateIssue({ inspectionId }) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ issueId, status, markedForSuggestion, managerNote, suggestedClausesSelected }) => updateIssueStatus(issueId, status, markedForSuggestion, managerNote, suggestedClausesSelected),
        // Optimistically update to the new value
        onMutate: (variables) => __awaiter(this, void 0, void 0, function* () {
            // Cancel any outgoing refetches for ALL issues queries (both list and grid) for this inspection
            yield queryClient.cancelQueries({ queryKey: iimReviewKeys.issuesAll(inspectionId) });
            yield queryClient.cancelQueries({ queryKey: iimReviewKeys.issuesGridAll(inspectionId) });
            // Snapshot previous values for ALL issues queries (list + grid)
            const previousQueries = [];
            queryClient.getQueriesData({ queryKey: iimReviewKeys.issuesAll(inspectionId) }).forEach(([queryKey, data]) => {
                previousQueries.push({ queryKey, data });
            });
            queryClient
                .getQueriesData({ queryKey: iimReviewKeys.issuesGridAll(inspectionId) })
                .forEach(([queryKey, data]) => {
                previousQueries.push({ queryKey, data });
            });
            const applyUpdate = (oldData) => {
                if (!(oldData === null || oldData === void 0 ? void 0 : oldData.issues))
                    return oldData;
                return Object.assign(Object.assign({}, oldData), { issues: oldData.issues.map((issue) => {
                        var _a;
                        return issue.id === variables.issueId
                            ? Object.assign(Object.assign({}, issue), { statusChanges: variables.status && variables.status !== issue.status
                                    ? [...(issue.statusChanges || []), variables.status]
                                    : issue.statusChanges, status: variables.status, managerNote: (_a = variables.managerNote) !== null && _a !== void 0 ? _a : issue.managerNote, markedForSuggestion: variables.markedForSuggestion, suggestedClausesSelected: variables.suggestedClausesSelected, updatedAt: new Date().toISOString() }) : issue;
                    }) });
            };
            // Optimistically update ALL cache entries that contain this issue (list + grid)
            queryClient.setQueriesData({ queryKey: iimReviewKeys.issuesAll(inspectionId) }, applyUpdate);
            queryClient.setQueriesData({ queryKey: iimReviewKeys.issuesGridAll(inspectionId) }, applyUpdate);
            // Return snapshots for rollback
            return { previousQueries };
        }),
        // If the mutation fails, rollback ALL cache entries
        onError: (err, variables, context) => {
            if (context === null || context === void 0 ? void 0 : context.previousQueries) {
                context.previousQueries.forEach(({ queryKey, data }) => {
                    queryClient.setQueryData(queryKey, data);
                });
            }
            console.error('Error updating issue:', err);
        }
    });
}
/**
 * Hook for bulk updating multiple IIM issues with optimistic updates
 * @returns Mutation object for bulk updating issue statuses and manager notes
 */
export function useBulkUpdateIssues({ inspectionId, issueIds }) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ status }) => bulkUpdateIssues(issueIds, status, issueIds.length === 0 ? inspectionId : undefined, issueIds.length === 0 ? ACTIONABLE_STATUSES : undefined),
        // Optimistic update
        onMutate: (variables) => __awaiter(this, void 0, void 0, function* () {
            // Cancel any outgoing refetches for ALL issues queries (list + grid)
            yield queryClient.cancelQueries({ queryKey: iimReviewKeys.issuesAll(inspectionId) });
            yield queryClient.cancelQueries({ queryKey: iimReviewKeys.issuesGridAll(inspectionId) });
            // Snapshot previous values for ALL issues queries (list + grid)
            const previousQueries = [];
            queryClient.getQueriesData({ queryKey: iimReviewKeys.issuesAll(inspectionId) }).forEach(([queryKey, data]) => {
                previousQueries.push({ queryKey, data });
            });
            queryClient
                .getQueriesData({ queryKey: iimReviewKeys.issuesGridAll(inspectionId) })
                .forEach(([queryKey, data]) => {
                previousQueries.push({ queryKey, data });
            });
            const applyUpdate = (oldData) => {
                if (!(oldData === null || oldData === void 0 ? void 0 : oldData.issues))
                    return oldData;
                return Object.assign(Object.assign({}, oldData), { issues: oldData.issues.map((issue) => issueIds.includes(issue.id)
                        ? Object.assign(Object.assign({}, issue), { status: variables.status, updatedAt: new Date().toISOString() }) : issue) });
            };
            // Optimistically update ALL cache entries (list + grid)
            queryClient.setQueriesData({ queryKey: iimReviewKeys.issuesAll(inspectionId) }, applyUpdate);
            queryClient.setQueriesData({ queryKey: iimReviewKeys.issuesGridAll(inspectionId) }, applyUpdate);
            return { previousQueries };
        }),
        // Roll back ALL cache entries on error
        onError: (err, variables, context) => {
            if (context === null || context === void 0 ? void 0 : context.previousQueries) {
                context.previousQueries.forEach(({ queryKey, data }) => {
                    queryClient.setQueryData(queryKey, data);
                });
            }
            console.error('Error bulk updating issues:', err);
        },
        // Always refetch after error or success to ensure we have the correct data
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: iimReviewKeys.issuesAll(inspectionId) });
            void queryClient.invalidateQueries({ queryKey: iimReviewKeys.issuesGridAll(inspectionId) });
        }
    });
}
/**
 * Hook for bulk marking/unmarking multiple IIM issues for suggestion without changing status
 * @returns Mutation object for bulk marking issues
 */
export function useBulkMarkIssuesForSuggestion({ inspectionId, issueIds }) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ marked }) => bulkMarkIssuesForSuggestion(issueIds, marked, issueIds.length === 0 ? inspectionId : undefined, issueIds.length === 0 ? ACTIONABLE_STATUSES : undefined),
        // Optimistic update
        onMutate: (variables) => __awaiter(this, void 0, void 0, function* () {
            // Cancel any outgoing refetches for ALL issues queries (list + grid)
            yield queryClient.cancelQueries({ queryKey: iimReviewKeys.issuesAll(inspectionId) });
            yield queryClient.cancelQueries({ queryKey: iimReviewKeys.issuesGridAll(inspectionId) });
            // Snapshot previous values for ALL issues queries (list + grid)
            const previousQueries = [];
            queryClient.getQueriesData({ queryKey: iimReviewKeys.issuesAll(inspectionId) }).forEach(([queryKey, data]) => {
                previousQueries.push({ queryKey, data });
            });
            queryClient
                .getQueriesData({ queryKey: iimReviewKeys.issuesGridAll(inspectionId) })
                .forEach(([queryKey, data]) => {
                previousQueries.push({ queryKey, data });
            });
            const applyUpdate = (oldData) => {
                if (!(oldData === null || oldData === void 0 ? void 0 : oldData.issues))
                    return oldData;
                return Object.assign(Object.assign({}, oldData), { issues: oldData.issues.map((issue) => issueIds.includes(issue.id)
                        ? Object.assign(Object.assign({}, issue), { markedForSuggestion: variables.marked, updatedAt: new Date().toISOString() }) : issue) });
            };
            // Optimistically update ALL cache entries (list + grid)
            queryClient.setQueriesData({ queryKey: iimReviewKeys.issuesAll(inspectionId) }, applyUpdate);
            queryClient.setQueriesData({ queryKey: iimReviewKeys.issuesGridAll(inspectionId) }, applyUpdate);
            return { previousQueries };
        }),
        // Roll back ALL cache entries on error
        onError: (err, variables, context) => {
            if (context === null || context === void 0 ? void 0 : context.previousQueries) {
                context.previousQueries.forEach(({ queryKey, data }) => {
                    queryClient.setQueryData(queryKey, data);
                });
            }
            console.error('Error bulk marking issues:', err);
        },
        // Always refetch after error or success to ensure we have the correct data
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: iimReviewKeys.issuesAll(inspectionId) });
            void queryClient.invalidateQueries({ queryKey: iimReviewKeys.issuesGridAll(inspectionId) });
        }
    });
}
// Default export for backward compatibility
export default useUpdateIssue;
